import { GetStaticProps } from 'next';
import { Router } from 'next/router';
import React, { FunctionComponent, useEffect } from 'react';

import GroupList from 'components/GroupList/GroupList';
import LinkButton from 'components/LinkButton/LinkButton';
import SidesText from 'components/SidesText/SidesText';
import Text from 'components/Text/Text';
import { useCopyStore } from 'store';
import { CopyStoreType } from 'store/copy.types';
import { queryCMS } from 'utils/cms/api';
import { aboutPageQuery } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { ROUTES } from 'utils/routes';
import { fadeInUpMotion, pageMotionProps } from 'utils/styles/animations';

import * as S from './AboutPage.styles';

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const response = await queryCMS({
    query: aboutPageQuery({ locale }),
  });

  const { header, footer, aboutPage: page, sharedCopy } = response;
  if (!page || !header || !footer) return { notFound: true };

  const {
    title,
    subtitle,
    head,
    sideTexts,
    servicesTitle,
    services,
    showMoreText,
  } = page;
  const { menu: headerMenu } = header;
  const {
    socialMedia,
    locations,
    name: footerName,
    menu: footerMenu,
    backgroundColor,
  } = footer;

  return {
    props: {
      initialCopy: {
        head,
        header: {
          menu: headerMenu,
        },
        footer: {
          name: footerName,
          socialMedia,
          locations,
          menu: footerMenu,
          backgroundColor,
        },
        about: {
          title,
          subtitle,
          sideTexts,
          servicesTitle,
          services,
          showMoreText,
        },
        global: sharedCopy.global,
      },
    },
    revalidate: ISR_TIMEOUT,
  };
};

interface AboutPageProps {
  initialCopy: {
    head: CopyStoreType['copy']['head'];
    header: CopyStoreType['copy']['header'];
    about: CopyStoreType['copy']['about'];
    footer: CopyStoreType['copy']['footer'];
    global: CopyStoreType['copy']['global'];
  };
  router: Router;
}

const AboutPage: FunctionComponent<AboutPageProps> = ({
  initialCopy,
  router,
}) => {
  const { copy, setCopy } = useCopyStore();

  useEffect(() => {
    setCopy(initialCopy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCopy]);

  if (!copy.about) return null;

  return (
    <S.AboutPageWrapper {...pageMotionProps}>
      <S.PageTitleWrapper
        aria-label={copy.about.title}
        {...fadeInUpMotion}
        transition={{ delay: 1.2, duration: 0.6 }}
      >
        <Text as="h2" text={copy.about.title} size="heading1" />
      </S.PageTitleWrapper>

      <S.SidesTextWrapper id="sideText" {...fadeInUpMotion}>
        <SidesText data={copy.about.sideTexts} />
      </S.SidesTextWrapper>

      <S.Wrapper id="services">
        <S.PageTitleWrapper
          aria-label={copy.about.servicesTitle}
          {...fadeInUpMotion}
          transition={{ delay: 1.2, duration: 0.6 }}
        >
          <Text as="h2" text={copy.about.servicesTitle} size="heading1" />
        </S.PageTitleWrapper>

        <GroupList items={copy.about.services} />

        <S.BottomBar>
          <LinkButton
            onClick={() => router.push(ROUTES.SERVICES)}
            text={copy.about.showMoreText}
            size="heading3"
          />
        </S.BottomBar>
      </S.Wrapper>
    </S.AboutPageWrapper>
  );
};

export default AboutPage;
